import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
  TextField,
  MenuItem,
  Stack,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { IService, IAppointment, WeeklySchedule } from '.';
import Loader from 'src/components/Loader';
import EnhancedTable from 'src/components/enhanced-table/EnhancedTable';
import {
  EnhancedTableColumn,
  EnhancedTableColumnType
} from 'src/components/enhanced-table';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TableChartIcon from '@mui/icons-material/TableChart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CategoryIcon from '@mui/icons-material/Category';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  format,
  isSameMonth,
  isSameDay,
  parseISO,
  isAfter,
  isBefore
} from 'date-fns';
import MainPageTitle from 'src/components/layout-components/main-layout/MainPageTitle';
import { IConfirmation } from 'src/redux/confirmation/types';
import { OpenConfirmation } from 'src/redux/confirmation/confirmationReducer';

type LanguageTab = 'en' | 'ar' | 'fr';
type ViewMode = 'calendar' | 'table';
type StatusFilter = 'All' | 'Pending' | 'Confirmed' | 'Processed' | 'Cancelled';

interface IAppointmentListing {
  id: string;
  contactName: string;
  appointmentDate: string;
  startTime: string;
  endTime: string;
  status: string;
}

const dayKeys: (keyof WeeklySchedule)[] = [
  'monday','tuesday','wednesday','thursday','friday','saturday','sunday'
];

const dayLabels: Record<keyof WeeklySchedule, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday'
};

const statusChipColors = {
  Confirmed: { backgroundColor: 'green', color: 'white' },
  Pending: { backgroundColor: 'orange', color: 'white' },
  Cancelled: { backgroundColor: 'red', color: 'white' },
  Processed: { backgroundColor: 'blue', color: 'white' }
} as const;

const languageLabels: Record<LanguageTab, string> = {
  en: 'EN',
  ar: 'AR',
  fr: 'FR'
};

const ServiceDetails: React.FC = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [service, setService] = useState<IService | null>(null);
  const [appointments, setAppointments] = useState<IAppointment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Language (single source of truth)
  const [activeLangTab, setActiveLangTab] = useState<LanguageTab>('en');

  // UI state
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectedAppointment, setSelectedAppointment] = useState<IAppointment | null>(null);
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState<boolean>(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState<string>(''); // yyyy-MM-dd
  const [toDate, setToDate] = useState<string>('');

  // Exception dialog state
  const [openExceptionDialog, setOpenExceptionDialog] = useState<boolean>(false);
  const [exceptionDate, setExceptionDate] = useState<string>('');
  const [exceptionClosed, setExceptionClosed] = useState<boolean>(true);
  const [exceptionWindows, setExceptionWindows] = useState<{ start: string; end: string }[]>([
    { start: '09:00', end: '17:00' }
  ]);

  // Collapsible sections
  const [operatingHoursExpanded, setOperatingHoursExpanded] = useState<boolean>(false);

  // ---- Data fetch ----------------------------------------------------------
  const fetchAll = useCallback(async () => {
    if (!serviceId) return;
    try {
      setLoading(true);
      const [svcRes, apptRes] = await Promise.all([
        DataService.get(`api/Service/get/${serviceId}`),
        DataService.get(`api/Appointment/service/${serviceId}`)
      ]);

      if (svcRes.ok) {
        const s = await svcRes.json();
        setService(s.data);
      } else {
        dispatch(showAlert({ message: 'Failed to fetch service details', type: 'error' }));
      }

      if (apptRes.ok) {
        const a = await apptRes.json();
        setAppointments(a.data || []);
      } else {
        dispatch(showAlert({ message: 'Failed to fetch appointments', type: 'error' }));
      }
    } catch (e) {
      dispatch(showAlert({ message: 'Error loading data', type: 'error' }));
    } finally {
      setLoading(false);
    }
  }, [serviceId, dispatch]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ---- Handlers ------------------------------------------------------------
  const handleLangTab = (_: React.SyntheticEvent | null, v: LanguageTab | null) => {
    if (v) setActiveLangTab(v);
  };

  const handleViewMode = (_: any, v: ViewMode | null) => v && setViewMode(v);
  const handlePrevMonth = () => setCurrentMonth((d) => subMonths(d, 1));
  const handleNextMonth = () => setCurrentMonth((d) => addMonths(d, 1));

  const openAppointment = (a: IAppointment) => { setSelectedAppointment(a); setAppointmentDialogOpen(true); };
  const closeDialog = () => { setAppointmentDialogOpen(false); setSelectedAppointment(null); };

  const getStatusSx = (status: string) => statusChipColors[status as keyof typeof statusChipColors]
    ?? { backgroundColor: 'gray', color: 'white' };

  // Exception handlers
  const openAddExceptionDialog = useCallback(() => {
    setExceptionDate('');
    setExceptionClosed(true);
    setExceptionWindows([{ start: '09:00', end: '17:00' }]);
    setOpenExceptionDialog(true);
  }, []);

  const handleCloseExceptionDialog = useCallback(() => {
    setOpenExceptionDialog(false);
    setExceptionDate('');
    setExceptionClosed(true);
    setExceptionWindows([{ start: '09:00', end: '17:00' }]);
  }, []);

  const handleAddTimeWindow = useCallback(() => {
    setExceptionWindows([...exceptionWindows, { start: '09:00', end: '17:00' }]);
  }, [exceptionWindows]);

  const handleRemoveTimeWindow = useCallback((index: number) => {
    setExceptionWindows(exceptionWindows.filter((_, i) => i !== index));
  }, [exceptionWindows]);

  const handleWindowChange = useCallback((index: number, field: 'start' | 'end', value: string) => {
    const updated = [...exceptionWindows];
    updated[index][field] = value;
    setExceptionWindows(updated);
  }, [exceptionWindows]);

  const handleSubmitException = useCallback(async () => {
    if (!exceptionDate) {
      dispatch(showAlert({ message: 'Please select a date', type: 'error' }));
      return;
    }

    const payload = {
      date: exceptionDate,
      closed: exceptionClosed,
      windows: exceptionClosed ? [] : exceptionWindows
    };

    try {
      const result = await DataService.post(
        `api/Service/${serviceId}/exceptions/add`,
        payload
      );
      if (result.ok) {
        dispatch(showAlert({ message: 'Exception added successfully!', type: 'success' }));
        handleCloseExceptionDialog();
        fetchAll();
      } else {
        dispatch(showAlert({ message: 'Failed to add exception', type: 'error' }));
      }
    } catch (error) {
      dispatch(showAlert({ message: 'Error adding exception', type: 'error' }));
    }
  }, [exceptionDate, exceptionClosed, exceptionWindows, serviceId, dispatch, handleCloseExceptionDialog, fetchAll]);

  const deleteException = useCallback((date: string) => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Confirmation',
      message: `Are you sure you want to delete the exception for ${date}?`,
      onSubmit: async () => {
        try {
          const result = await DataService.delete(
            `api/Service/${serviceId}/exceptions/remove?date=${date}`,
            {}
          );
          if (result.ok) {
            dispatch(showAlert({ message: 'Exception deleted successfully!', type: 'success' }));
            fetchAll();
          } else {
            dispatch(showAlert({ message: 'Failed to delete exception', type: 'error' }));
          }
        } catch (error) {
          dispatch(showAlert({ message: 'Error deleting exception', type: 'error' }));
        }
      }
    };
    dispatch(OpenConfirmation(confirmation));
  }, [serviceId, dispatch, fetchAll]);

  // ---- Derived: filtered appointments --------------------------------------
  const filteredAppointments = useMemo(() => {
    return appointments.filter(a => {
      const matchesStatus = statusFilter === 'All' || a.status === statusFilter;
      const matchesSearch =
        !search ||
        (a.contactName ?? '').toLowerCase().includes(search.toLowerCase()) ||
        (a.serviceName ?? '').toLowerCase().includes(search.toLowerCase());

      const date = parseISO(a.appointmentDate);
      const afterFrom = !fromDate || !isBefore(date, parseISO(fromDate));
      const beforeTo = !toDate || !isAfter(date, parseISO(toDate));
      return matchesStatus && matchesSearch && afterFrom && beforeTo;
    });
  }, [appointments, statusFilter, search, fromDate, toDate]);

  const upcomingCount = useMemo(() => {
    const now = new Date();
    return appointments.filter(a => isAfter(parseISO(a.appointmentDate), now)).length;
  }, [appointments]);

  // ---- Table data ----------------------------------------------------------
  const tableColumns: EnhancedTableColumn[] = [
    { id: 'contactName', label: 'Contact', type: EnhancedTableColumnType.TEXT },
    { id: 'serviceName', label: 'Service', type: EnhancedTableColumnType.TEXT },
    { id: 'appointmentDate', label: 'Date', type: EnhancedTableColumnType.TEXT },
    { id: 'startTime', label: 'Start', type: EnhancedTableColumnType.TEXT },
    { id: 'endTime', label: 'End', type: EnhancedTableColumnType.TEXT },
    {
      id: 'status',
      label: 'Status',
      type: EnhancedTableColumnType.COLORED_CHIP,
      chipColors: statusChipColors as any
    }
  ];

  const tableData: Record<string, IAppointmentListing & { serviceName: string }> = {};
  filteredAppointments.forEach((a) => {
    tableData[a.id] = {
      id: a.id,
      contactName: a.contactName || 'N/A',
      serviceName: a.serviceName || 'N/A',
      appointmentDate: new Date(a.appointmentDate).toLocaleDateString(),
      startTime: a.startTime,
      endTime: a.endTime,
      status: a.status
    };
  });

  // ---- Calendar helpers ----------------------------------------------------
  const appointmentsByDate = useMemo(() => {
    const grouped: Record<string, IAppointment[]> = {};
    filteredAppointments.forEach((a) => {
      const k = format(parseISO(a.appointmentDate), 'yyyy-MM-dd');
      (grouped[k] ||= []).push(a);
    });
    return grouped;
  }, [filteredAppointments]);

  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows: JSX.Element[] = [];
    const headerNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

    // header
    rows.push(
      <Grid container spacing={0} key="header">
        {headerNames.map(h => (
          <Grid item xs={12/7} key={h}>
            <Box sx={{ textAlign:'center', py:1, fontWeight:'bold', borderBottom:1, borderColor:'divider' }}>{h}</Box>
          </Grid>
        ))}
      </Grid>
    );

    let day = startDate;
    while (day <= endDate) {
      const cells: JSX.Element[] = [];
      for (let i=0;i<7;i++) {
        const dateKey = format(day, 'yyyy-MM-dd');
        const dayAppointments = appointmentsByDate[dateKey] || [];
        const isCurrent = isSameMonth(day, monthStart);
        cells.push(
          <Grid item xs={12/7} key={day.toISOString()}>
            <Box
              sx={{
                minHeight: 110,
                border: 1,
                borderColor: 'divider',
                p: 1,
                bgcolor: isCurrent ? 'background.paper' : 'action.hover'
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: isSameDay(day, new Date()) ? 700 : 400 }}>
                {format(day,'d')}
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {dayAppointments.slice(0, 3).map((a) => (
                  <Chip
                    key={a.id}
                    size="small"
                    label={`${a.startTime} • ${(a.contactName ?? 'N/A').slice(0, 12)}`}
                    onClick={() => openAppointment(a)}
                    sx={{
                      ...getStatusSx(a.status),
                      height: 22,
                      '& .MuiChip-label': {
                        px: 0.75,
                        fontSize: '0.72rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%'
                      },
                      cursor: 'pointer',
                      maxWidth: '100%'
                    }}
                  />
                ))}
                {dayAppointments.length > 3 && (
                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
                    onClick={() => openAppointment(dayAppointments[3])}
                  >
                    +{dayAppointments.length - 3} more
                  </Typography>
                )}
              </Stack>
            </Box>
          </Grid>
        );
        day = addDays(day, 1);
      }
      rows.push(<Grid container spacing={0} key={day.toISOString()}>{cells}</Grid>);
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb: 1 }}>
          <IconButton onClick={handlePrevMonth}><ChevronLeftIcon /></IconButton>
          <Typography variant="h5">{format(currentMonth, 'MMMM yyyy')}</Typography>
          <IconButton onClick={handleNextMonth}><ChevronRightIcon /></IconButton>
        </Box>
        {rows}
      </Box>
    );
  };

  // ---- Render --------------------------------------------------------------
  if (loading) {
    return (
      <Box>
        <Helmet><title>Service Details</title></Helmet>
        <MainPageTitle title="Service Details" />
        <Loader />
      </Box>
    );
  }

  if (!service) {
    return (
      <Box>
        <Helmet><title>Service Not Found</title></Helmet>
        <MainPageTitle title="Service Not Found" />
        <Typography>Service not found.</Typography>
      </Box>
    );
  }

  // language-aware fields (fallback to EN)
  const name = service.name[activeLangTab] || service.name.en;
  const description = service.description[activeLangTab] || service.description.en;
  const requirementsHtml =
    (service.requirements?.[activeLangTab] ?? service.requirements?.en ?? '');
  const termsHtml =
    (service.termsOfServices?.[activeLangTab] ?? service.termsOfServices?.en ?? '');

  const renderHours = (ws?: WeeklySchedule) => (
    <Grid container spacing={1.5}>
      {dayKeys.map((k) => {
        const d = ws?.[k];
        const isOpen = d?.open && (d?.windows?.length ?? 0) > 0;
        const label = dayLabels[k];
        const slot = isOpen ? `${d!.windows[0].start} – ${d!.windows[0].end}` : 'Closed';
        return (
          <Grid item xs={12} sm={6} md={4} key={k}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <AccessTimeIcon fontSize="small" sx={{ flexShrink: 0 }} />
              <Typography sx={{ minWidth: 88, whiteSpace: 'nowrap' }} variant="body2">{label}</Typography>
              <Chip
                size="small"
                label={slot}
                color={isOpen ? 'success' : 'default'}
                variant={isOpen ? 'filled' : 'outlined'}
                sx={{ flexShrink: 0 }}
              />
            </Stack>
          </Grid>
        );
      })}
    </Grid>
  );

  // Requirements Panel (no tabs; bound to activeLangTab from header)
  const RequirementsPanel = (
    <Card sx={{ mt: 3, mb: 3 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <InfoOutlinedIcon fontSize="small" />
          <Typography variant="h6">Requirements</Typography>
        </Stack>
        {/* NOTE: HTML is authored by admins in your own UI. If external, sanitize before rendering. */}
        <Box
          sx={{ typography: 'body1' }}
          dangerouslySetInnerHTML={{ __html: requirementsHtml }}
        />
      </CardContent>
    </Card>
  );

  // Terms of Services Panel (bound to activeLangTab from header)
  const TermsOfServicesPanel = (
    <Card sx={{ mt: 3, mb: 3 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <InfoOutlinedIcon fontSize="small" />
          <Typography variant="h6">Terms of Services</Typography>
        </Stack>
        {/* NOTE: HTML is authored by admins in your own UI. If external, sanitize before rendering. */}
        <Box
          sx={{ typography: 'body1' }}
          dangerouslySetInnerHTML={{ __html: termsHtml }}
        />
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Helmet><title>{name} - Details</title></Helmet>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: '#4f8dfd' }}>
          Service Details
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>Back</Button>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchAll}><RefreshIcon /></IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Service header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* Top row: name/desc on left, language selector on right */}
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
              {service.iconUrl && <Avatar src={service.iconUrl} alt={name} sx={{ width: 64, height: 64, flexShrink: 0 }} />}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h4" sx={{ mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</Typography>
                {description && (
                  <Typography variant="body1" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {description}
                  </Typography>
                )}
              </Box>
            </Stack>

            {/* Single language selector (global) */}
            <ToggleButtonGroup
              value={activeLangTab}
              exclusive
              onChange={handleLangTab}
              size="small"
              aria-label="Language"
            >
              <ToggleButton value="en" aria-label="English">{languageLabels.en}</ToggleButton>
              <ToggleButton value="ar" aria-label="Arabic">{languageLabels.ar}</ToggleButton>
              <ToggleButton value="fr" aria-label="French">{languageLabels.fr}</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {/* Meta */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                <CategoryIcon fontSize="small" sx={{ flexShrink: 0 }} />
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Category:</Typography>
                <Chip size="small" label={service.category || 'Other'} sx={{ flexShrink: 0 }} />
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                <AccessTimeIcon fontSize="small" sx={{ flexShrink: 0 }} />
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Duration:</Typography>
                <Chip size="small" label={`${service.serviceTimeMinutes ?? 0} min`} sx={{ flexShrink: 0 }} />
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                <LocalOfferIcon fontSize="small" sx={{ flexShrink: 0 }} />
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Fee:</Typography>
                <Chip
                  size="small"
                  label={
                    service.fee?.amount != null
                      ? `${service.fee.amount} ${service.fee.currency ?? 'USD'}`
                      : 'Free'
                  }
                  color={service.fee?.amount ? 'primary' : 'default'}
                  variant={service.fee?.amount ? 'filled' : 'outlined'}
                  sx={{ flexShrink: 0 }}
                />
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                <EventAvailableIcon fontSize="small" />
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Limits:</Typography>
                <Chip
                  size="small"
                  label={`Max/day: ${service.maxAppointmentsPerDay ?? '—'}`}
                  sx={{ flexShrink: 0 }}
                />
                <Chip
                  size="small"
                  label={`Advance: ${service.maxAdvanceDays ?? '—'} days`}
                  sx={{ flexShrink: 0 }}
                />
              </Stack>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ cursor: 'pointer' }} onClick={() => setOperatingHoursExpanded(!operatingHoursExpanded)}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1">Operating Hours</Typography>
              <IconButton size="small">
                {operatingHoursExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Stack>
          </Box>

          {operatingHoursExpanded && (
            <Box sx={{ mt: 2 }}>
              {renderHours(service.weeklySchedule)}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Requirements */}
      {RequirementsPanel}

      {/* Terms of Services */}
      {TermsOfServicesPanel}

      {/* Exceptions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <EventBusyIcon fontSize="small" />
              <Typography variant="h5">Schedule Exceptions</Typography>
            </Stack>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAddExceptionDialog}
              size="small"
            >
              Add Exception
            </Button>
          </Stack>

          {service.exceptions && service.exceptions.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Time Windows</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {service.exceptions.map((exception, index) => (
                    <TableRow key={index}>
                      <TableCell>{exception.date}</TableCell>
                      <TableCell>
                        <Chip
                          label={exception.closed ? 'Closed' : 'Modified Hours'}
                          color={exception.closed ? 'error' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {exception.closed ? (
                          <Typography variant="body2" color="text.secondary">
                            Service closed
                          </Typography>
                        ) : exception.windows && exception.windows.length > 0 ? (
                          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                            {exception.windows.map((window, wIndex) => (
                              <Chip
                                key={wIndex}
                                label={`${window.start} - ${window.end}`}
                                size="small"
                                variant="outlined"
                                sx={{ flexShrink: 0 }}
                              />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No windows specified
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Delete Exception">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => deleteException(exception.date)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No exceptions configured. Click "Add Exception" to create one.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Appointments */}
      {/* <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h5">
              Appointments ({filteredAppointments.length}){' '}
              <Typography component="span" variant="body2" color="text.secondary">• Upcoming: {upcomingCount}</Typography>
            </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField
                select
                label="Status"
                size="small"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                sx={{ minWidth: 140 }}
              >
                {['All','Pending','Confirmed','Processed','Cancelled'].map(s => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </TextField>

              <TextField
                size="small"
                label="Search"
                placeholder="Contact or Service"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <TextField
                size="small"
                type="date"
                label="From"
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
              <TextField
                size="small"
                type="date"
                label="To"
                InputLabelProps={{ shrink: true }}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />

              <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewMode} size="small">
                <ToggleButton value="calendar"><CalendarMonthIcon sx={{ mr: 0.5 }} />Calendar</ToggleButton>
                <ToggleButton value="table"><TableChartIcon sx={{ mr: 0.5 }} />Table</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Stack>

          {viewMode === 'calendar' ? (
            renderCalendar()
          ) : (
            <EnhancedTable
              title=""
              columns={tableColumns}
              data={tableData}
              actions={[
                {
                  key: 'view',
                  title: 'View',
                  onClick: (selected: string[]) => {
                    if (selected.length > 0) {
                      const a = filteredAppointments.find(x => x.id === selected[0]);
                      if (a) openAppointment(a);
                    }
                  }
                }
              ]}
              emptyMessage='No appointments match your filters'
            />
          )}
        </CardContent>
      </Card> */}

      {/* Appointment Details Dialog */}
      <Dialog open={appointmentDialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <Typography variant="h6">Appointment Details</Typography>
            {selectedAppointment && (
              <Chip label={selectedAppointment.status} size="small" sx={getStatusSx(selectedAppointment.status)} />
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedAppointment && (
            <Box sx={{ display:'grid', gridTemplateColumns:'1fr', rowGap: 1.5 }}>
              <InfoLine label="Contact" value={selectedAppointment.contactName || 'N/A'} />
              <InfoLine label="Service" value={selectedAppointment.serviceName || service?.name.en || 'N/A'} />
              <InfoLine label="Date" value={format(parseISO(selectedAppointment.appointmentDate), 'MMMM dd, yyyy')} />
              <InfoLine label="Time" value={`${selectedAppointment.startTime} – ${selectedAppointment.endTime}`} />
              <InfoLine label="Status" value={selectedAppointment.status} />
              <InfoLine label="Appointment ID" mono value={selectedAppointment.id} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Exception Dialog */}
      <Dialog open={openExceptionDialog} onClose={handleCloseExceptionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Service Exception</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Date"
              type="date"
              fullWidth
              value={exceptionDate}
              onChange={(e) => setExceptionDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={exceptionClosed}
                  onChange={(e) => setExceptionClosed(e.target.checked)}
                />
              }
              label="Service Closed on this Date"
            />

            {!exceptionClosed && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Time Windows
                </Typography>
                {exceptionWindows.map((window, index) => (
                  <Stack direction="row" spacing={1} key={index} sx={{ mb: 1 }}>
                    <TextField
                      label="Start Time"
                      type="time"
                      value={window.start}
                      onChange={(e) => handleWindowChange(index, 'start', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                    <TextField
                      label="End Time"
                      type="time"
                      value={window.end}
                      onChange={(e) => handleWindowChange(index, 'end', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                    <IconButton
                      onClick={() => handleRemoveTimeWindow(index)}
                      disabled={exceptionWindows.length === 1}
                      color="error"
                      size="small"
                    >
                      <RemoveIcon />
                    </IconButton>
                  </Stack>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddTimeWindow}
                  variant="outlined"
                  size="small"
                >
                  Add Time Window
                </Button>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExceptionDialog}>Cancel</Button>
          <Button onClick={handleSubmitException} variant="contained">
            Add Exception
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// small helper for the dialog
const InfoLine: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <Box>
    <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
    <Typography variant="body1" sx={mono ? { fontFamily: 'monospace' } : undefined}>{value}</Typography>
  </Box>
);

export default ServiceDetails;
